import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./MlaCitationContent.css";

// ---- tiny scroll-reveal wrapper (mirrors the reference design's fade-up-on-scroll) ----
const Reveal = ({ as: Tag = "div", className = "", style, children, id }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      id={id}
      style={style}
      className={`mla-reveal${visible ? " mla-visible" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </Tag>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.5 3.8 6 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-6-3.8-9s1.3-6.5 3.8-9z" strokeWidth="1.8" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" strokeLinejoin="round" />
  </svg>
);

const QuoteIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M7 8c-1.8 1-3 2.8-3 5a3 3 0 003 3M17 8c-1.8 1-3 2.8-3 5a3 3 0 003 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ExportIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3v13m0 0l-5-5m5 5l5-5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 20h16" strokeLinecap="round" />
  </svg>
);

const FolderIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 7a2 2 0 012-2h4l2 2.5h8A2 2 0 0121 9.5V18a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" strokeLinejoin="round" />
  </svg>
);

const MLA_COMPONENTS = [
  { badge: "01", title: "Author", desc: "Listed last name first for the first author, followed by any additional authors in normal order. Left off entirely when a source has no named author.", tags: ["Works Cited", "Alphabetizing"] },
  { badge: "02", title: "Title of source", desc: "The title of the specific piece you used, in quotation marks for a short work like an article, or italicized for a standalone work like a book or a film.", tags: ["Quotes vs italics"] },
  { badge: "03", title: "Title of container", desc: "The larger source that holds the piece you cited, such as a journal that holds an article, a website that holds a page, or a streaming service that holds an episode.", tags: ["Nested containers"] },
  { badge: "04", title: "Contributors", desc: "Editors, translators, and illustrators, listed by role when their work shaped the version of the source you used, such as \"edited by\" or \"translated by.\"", tags: ["Optional element"] },
  { badge: "05", title: "Publisher and date", desc: "The organization responsible for the source and the date it was published, or the date you accessed it when no publication date is available.", tags: ["Access dates"] },
  { badge: "06", title: "Location", desc: "Where the source can be found: a page range in print, a URL for a webpage, or a DOI when the journal provides one, listed without \"http://\".", tags: ["URLs", "DOIs", "Page numbers"] },
];

const MLA_STEPS = [
  { title: "Choose your source type", desc: "Select website, book, journal article, video, newspaper, or report, so the generator knows which fields to ask for." },
  { title: "Paste the identifier", desc: "Add a URL, DOI, or ISBN, or type the source details in by hand if you already have them on hand." },
  { title: "Generate the citation", desc: "The tool arranges the author, title, container, publisher, and date into the order MLA expects." },
  { title: "Review the result", desc: "Check that the author name, italics, and punctuation match the source in front of you, and edit any field that needs a fix." },
  { title: "Copy it into your paper", desc: "Add the full Works Cited entry to your reference list and drop the matching in-text citation wherever you quote or paraphrase the source." },
];

const MLA_SOURCE_TYPES = [
  { tag: "Website", title: "Website Citation Generator", desc: "Cite webpages, blog posts, and online articles with the site name treated as the container." },
  { tag: "Book", title: "Book Citation Generator", desc: "Create citations for print books, ebooks, and edited collections, including chapter-level entries." },
  { tag: "Journal Article", title: "Journal Article Generator", desc: "Generate citations for scholarly articles, including volume, issue, and page range or DOI." },
  { tag: "Video", title: "Video Citation Generator", desc: "Cite YouTube videos, lectures, and streamed footage, with the uploader listed as the contributor." },
  { tag: "Newspaper", title: "Newspaper Citation Generator", desc: "Format citations for print and online newspaper articles, including section and edition where relevant." },
  { tag: "Report", title: "Report Citation Generator", desc: "Cite research reports, white papers, and government publications with the issuing body as author." },
];

const MLA_EXAMPLES = [
  {
    tag: "Journal Article · MLA",
    ref: <>Smith, Jane, and Robert Lee. "Understanding Research Habits Among Graduate Students." <i>Journal of Academic Writing</i>, vol. 15, no. 3, 2026, pp. 115&ndash;35.</>,
    intext: "(Smith and Lee 120)",
  },
  {
    tag: "Website · MLA",
    ref: <>"How AI Tools Help Students Write Better." <i>ResearchPal Blog</i>, 2026, researchpal.co.</>,
    intext: '("How AI Tools")',
  },
];

const MLA_WHY_ITEMS = [
  { icon: <CheckIcon />, title: "MLA-specific formatting", desc: "Author order, italics, and punctuation follow the ninth-edition MLA Handbook." },
  { icon: <FolderIcon />, title: "Every source type covered", desc: "Websites, books, journals, videos, newspapers, and reports, each with its own fields." },
  { icon: <EditIcon />, title: "Editable by hand", desc: "Fix a missing detail or an unusual source without starting the entry over." },
  { icon: <QuoteIcon />, title: "In-text and full reference", desc: "Get the Works Cited entry and its matching parenthetical citation together." },
  { icon: <ExportIcon />, title: "Copy, save, or export", desc: "Send finished citations straight to your document or bibliography list." },
  { icon: <GlobeIcon />, title: "Bibliography support", desc: "Keep a running Works Cited list across a long paper with many sources." },
];

const MLA_PLAN_COMPARISON = [
  { feature: "Generate APA citations", free: true, premium: true },
  { feature: "Generate MLA citations", free: true, premium: true },
  { feature: "Generate Chicago citations", free: true, premium: true },
  { feature: "Copy citations", free: true, premium: true },
  { feature: "Save bibliography lists", free: "limited", premium: "unlimited" },
  { feature: "Export to Word or Google Docs", free: "limited", premium: true },
  { feature: "Bulk citation import", free: false, premium: true },
];

const MLA_FAQS = [
  { q: "Is this MLA citation generator free to use?", a: "Yes. Every core feature, including Works Cited entries and in-text citations for MLA format, is free, with no sign-up required to try it." },
  { q: "Which MLA edition does this tool follow?", a: "Citations follow the MLA Handbook, ninth edition, which organizes every source around nine core elements rather than fixed templates for each source type." },
  { q: "What can I paste into the generator?", a: "A URL, a DOI, an ISBN, or the raw details of a source such as an author and title. The tool works with whatever identifying information you already have." },
  { q: "Does it create both the Works Cited entry and the in-text citation?", a: "Yes. Each generated result includes the full reference-list entry along with the matching parenthetical citation you place in the body of your paper." },
  { q: "Can I edit a citation after it is generated?", a: "Yes. Every field remains editable, so you can correct a name, add a missing contributor, or adjust a date without rebuilding the entry from scratch." },
  { q: "What is a container in MLA format?", a: "A container is the larger source that holds the piece you are citing, such as a journal holding an article, a streaming platform holding an episode, or a website holding a page." },
  { q: "Do I need an account to use this free MLA citation generator?", a: "No. You can generate and copy citations without creating an account. An account is only useful if you want to save a running bibliography across sessions." },
  { q: "How does MLA handle a source with no listed author?", a: "When no author is named, the entry starts with the title of the source instead, and the in-text citation uses a shortened version of that title." },
  { q: "Can this tool cite a YouTube video or other online video?", a: "Yes. Select the video source type, and the generator treats the platform as the container and the uploader as the contributor, following MLA's guidance for audiovisual sources." },
  { q: "What is the difference between MLA and APA citations?", a: "MLA is built around a flexible container system and is common in literature, arts, and humanities courses, while APA uses a fixed author-date format common in the social sciences." },
  { q: "Does the generator handle DOIs for journal articles?", a: "Yes. When a DOI is available, it replaces the URL as the location element, since MLA treats a DOI as a more stable identifier than a web address." },
  { q: "How do I cite a source with multiple authors in MLA?", a: "List the first author last name first, followed by the remaining authors in normal order. Sources with three or more authors use the first author's name followed by 'et al.'" },
  { q: "Can I use this MLA format citation generator on my phone?", a: "Yes. The tool works on any device with a browser, so you can generate a citation from a phone or tablet as easily as from a computer." },
  { q: "Does the generator save my source details?", a: "Source details you enter stay private to your session unless you choose to save them to a bibliography list with an account." },
  { q: "What if my source is missing a publication date?", a: "MLA allows you to use the date you accessed the source instead, placed at the end of the citation when no publication date is available." },
  { q: "Can I export my citations to Word or Google Docs?", a: "Yes. Finished citations can be copied directly into a document, and premium accounts support exporting a full bibliography list at once." },
  { q: "Is this an MLA online citation generator or does it require installation?", a: "It runs entirely in the browser. There is nothing to install, and no software update is needed to keep the formatting current." },
  { q: "How do page numbers work in an MLA in-text citation?", a: "Include the author's last name and the specific page number in parentheses, without a comma between them, such as (Smith 42)." },
  { q: "Can I cite a chapter from an edited book?", a: "Yes. Select the book source type and add the chapter author, chapter title, and the book's editor, and the generator places each in its correct position." },
  { q: "Does this MLA citation generator support bulk citation import?", a: "Bulk import is available on the premium plan, useful for building a bibliography from a long reading list at once rather than one entry at a time." },
];

const FAQ_BATCH = 5;

// Reference design closes any other open FAQ item when a new one opens (accordion behaviour).
const MlaFaqList = () => {
  const [revealed, setRevealed] = useState(FAQ_BATCH);
  const [openIdx, setOpenIdx] = useState(null);

  const toggle = (idx) => {
    setOpenIdx((prev) => (prev === idx ? null : idx));
    setRevealed((prev) => (prev < MLA_FAQS.length ? Math.min(prev + FAQ_BATCH, MLA_FAQS.length) : prev));
  };

  const visible = MLA_FAQS.slice(0, revealed);

  return (
    <>
      <div className="mla-faq-list">
        {visible.map((item, idx) => {
          const open = openIdx === idx;
          return (
            <div className={`mla-faq-item${open ? " mla-open" : ""}`} key={item.q}>
              <button type="button" className="mla-faq-q" aria-expanded={open} onClick={() => toggle(idx)}>
                <span><span className="mla-num">{String(idx + 1).padStart(2, "0")}</span>{item.q}</span>
                <span className="mla-plus" />
              </button>
              <div className="mla-faq-a">
                <div className="mla-faq-a-inner">{item.a}</div>
              </div>
            </div>
          );
        })}
      </div>
      {revealed < MLA_FAQS.length && (
        <div className="mla-load-more-wrap">
          <button type="button" className="mla-btn mla-btn-outline" onClick={() => setRevealed((prev) => Math.min(prev + FAQ_BATCH, MLA_FAQS.length))}>
            Show more questions
          </button>
        </div>
      )}
    </>
  );
};

const MlaCitationContent = () => {
  return (
    <div className="mla-lp">
      <section className="mla-section mla-intro">
        <div className="mla-wrap">
          <Reveal>
            <span className="mla-eyebrow mla-center" style={{ display: "block", textAlign: "center" }}>Free MLA Format Citation Generator</span>
            <h1>Free MLA Citation Generator: Works Cited + In-Text</h1>
            <p className="mla-lead">The MLA format citation generator on this page takes the source details you already have (an author, a title, a publication date) and arranges them the way the MLA Handbook, ninth edition, expects. You get a Works Cited entry and a matching in-text citation, formatted the same way whether the source is a website, a book, a journal article, or a video.</p>
          </Reveal>
          <Reveal className="mla-chip-row">
            <div className="mla-chip"><span className="mla-dot" />Works Cited entries</div>
            <div className="mla-chip"><span className="mla-dot" />In-text citations</div>
            <div className="mla-chip"><span className="mla-dot" />Container-based formatting</div>
            <div className="mla-chip"><span className="mla-dot" />Multiple source types</div>
            <div className="mla-chip"><span className="mla-dot" />Copy, save, or export</div>
          </Reveal>
          <Reveal className="mla-cta-btn">
            <a href="#citation-tool" className="mla-btn mla-btn-outline">Generate My MLA Citation Free</a>
          </Reveal>
        </div>
      </section>

      <section className="mla-section mla-section-alt">
        <div className="mla-wrap">
          <Reveal className="mla-quote-card">
            <p>An MLA citation generator takes the raw details of a source (who wrote it, what it is called, where it was published) and arranges them in the order the Modern Language Association sets out, so you are not measuring commas and italics by hand.</p>
          </Reveal>
          <Reveal className="mla-two-col">
            <div className="mla-info-card">
              <h3>How it works</h3>
              <p>Instead of checking a style manual line by line, you enter what you know about the source, such as a URL, a DOI, an author or a title, and the tool places each piece where MLA expects it: author first, then title of source, then the container it appeared in, followed by publisher and date.</p>
            </div>
            <div className="mla-info-card">
              <h3>Why it helps</h3>
              <p>A missed period or a title left in regular type instead of italics is easy to overlook when a paper has thirty sources. This MLA online citation generator keeps every entry consistent, so your Works Cited page reads as though one careful hand built it from the first source to the last.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mla-section" id="mla-components">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">MLA Format, Explained</span>
            <h2>The parts of an MLA citation, one at a time</h2>
            <p>MLA ninth edition builds every citation from the same nine core elements. Learn what each one means before you start citing, and the rest of the format falls into place.</p>
          </Reveal>
          <Reveal className="mla-grid-6">
            {MLA_COMPONENTS.map((c) => (
              <div className="mla-style-card" key={c.title}>
                <span className="mla-badge">{c.badge}</span>
                <h3>{c.title}</h3>
                <p>{c.desc}</p>
                <div className="mla-tag-row">{c.tags.map((t) => <span key={t}>{t}</span>)}</div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="mla-section mla-section-alt" id="mla-how-it-works">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">Five Simple Steps</span>
            <h2>How to use this MLA citation generator</h2>
            <p>Every citation on this page follows the same short path from source to finished entry.</p>
          </Reveal>
          <div className="mla-steps-list">
            {MLA_STEPS.map((step, idx) => (
              <Reveal as="div" className="mla-step-row" key={step.title}>
                <div className="mla-step-num">{idx + 1}</div>
                <div className="mla-step-body"><h3>{step.title}</h3><p>{step.desc}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mla-section" id="mla-source-types">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">By Source Type</span>
            <h2>Generate MLA citations for any source</h2>
            <p>Each source type asks for slightly different details. Pick yours and the generator fills in the rest of the format.</p>
          </Reveal>
          <Reveal className="mla-grid-6">
            {MLA_SOURCE_TYPES.map((item) => (
              <div className="mla-style-card" key={item.tag}>
                <span className="mla-badge">{item.tag[0]}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <div className="mla-tag-row"><span>{item.tag}</span></div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="mla-section mla-section-alt" id="mla-examples">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">See It In Action</span>
            <h2>Example MLA citations</h2>
            <p>A finished entry looks like this once the details are placed in the right order.</p>
          </Reveal>
          <Reveal className="mla-example-grid">
            {MLA_EXAMPLES.map((ex) => (
              <div className="mla-example-card" key={ex.tag}>
                <div className="mla-example-tag">{ex.tag}</div>
                <p className="mla-example-body">{ex.ref}</p>
                <p className="mla-example-intext">In-text citation: <b>{ex.intext}</b></p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="mla-section">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">The Payoff</span>
            <h2>Why use this free MLA citation generator</h2>
            <p>Built for students, researchers, and anyone assembling a Works Cited page under a deadline.</p>
          </Reveal>
          <Reveal className="mla-why-grid">
            {MLA_WHY_ITEMS.map((w) => (
              <div className="mla-why-item" key={w.title}>
                <div className="mla-why-icon">{w.icon}</div>
                <div><h3>{w.title}</h3><p>{w.desc}</p></div>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="mla-section mla-section-alt">
        <div className="mla-wrap">
          <Reveal className="mla-section-head" style={{ marginBottom: "24px" }}>
            <span className="mla-eyebrow" style={{ color: '#10b981', fontWeight: 700, letterSpacing: '0.12em' }}>COMPARE PLANS</span>
            <h2 style={{ marginTop: "10px", fontFamily: 'var(--mla-serif)' }}>Free and premium features</h2>
          </Reveal>

          <Reveal className="mla-compare-wrap">
            <table className="mla-compare-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free Version</th>
                  <th>Premium Version</th>
                </tr>
              </thead>
              <tbody>
                {MLA_PLAN_COMPARISON.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td>
                      {row.free === true ? (
                        <span className="mla-yes">✓</span>
                      ) : row.free === false ? (
                        <span className="mla-no">✕</span>
                      ) : row.free === "limited" ? (
                        <span className="mla-pill-limited">Limited</span>
                      ) : null}
                    </td>
                    <td>
                      {row.premium === true ? (
                        <span className="mla-yes">✓</span>
                      ) : row.premium === "unlimited" ? (
                        <span className="mla-pill-unlimited">Unlimited</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section className="mla-section" id="mla-ai-research-assistant">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">AI Research Assistant</span>
            <h2>AI Research Assistant for MLA Research and Citations</h2>
          </Reveal>
          <Reveal className="mla-two-col">
            <div className="mla-info-card">
              <p style={{ marginBottom: "14px" }}>MLA citation formatting is especially common in literature, languages, humanities and other fields where students frequently work with books, journal articles, websites and other sources.</p>
              <p style={{ marginBottom: "14px" }}>ResearchPal's free MLA Citation Generator can help format your references, but the AI Research Assistant can support the research thinking that happens before and after citation creation.</p>
              <p>You can use the Agent to explore a topic, understand a source, discuss an argument, clarify terminology or investigate questions raised while reading academic material. ResearchPal's broader MLA workflow also connects citation generation with paper discovery, PDF analysis, Paper Insights and research organization.</p>
            </div>
            <div className="mla-info-card">
              <h3>Use the AI Research Assistant Alongside MLA Citations</h3>
              <p style={{ marginBottom: "10px" }}>It can help you:</p>
              <ul className="mla-ai-list">
                <li>Explore literature-related research questions</li>
                <li>Understand difficult academic sources</li>
                <li>Develop and refine essay arguments</li>
                <li>Discuss evidence before citing it</li>
                <li>Clarify concepts and terminology</li>
                <li>Connect sources with your research topic</li>
                <li>Continue research after generating an MLA citation</li>
              </ul>
            </div>
          </Reveal>
          <Reveal className="mla-ai-cta">
            <Link to="/ai-agent" className="mla-btn mla-btn-solid">Try AI Research Assistant</Link>
          </Reveal>
        </div>
      </section>

      <section className="mla-section mla-section-alt" id="mla-free-tools">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">More Free Tools</span>
            <h2>Explore ResearchPal's Free Tools</h2>
          </Reveal>

          <div className="mla-free-tools-group">
            <h3 className="mla-free-tools-group-title">ResearchPal Free Tools</h3>
            <Reveal className="mla-grid-6">
              <Link className="mla-free-tool-card" to="/paraphraser">
                <div><h3>Paraphraser</h3><p>Rewrite sentences and paragraphs while keeping the meaning.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/ai-agent">
                <div><h3>AI Agent</h3><p>Chat with an AI assistant built for research tasks.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/search">
                <div><h3>Search Papers</h3><p>Find and explore academic papers across sources.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/essay-writer">
                <div><h3>Essay Writer</h3><p>Draft structured essays with AI assistance.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/literature-review">
                <div><h3>Literature Review</h3><p>Summarize and organize sources for your review.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/paper-insights">
                <div><h3>Paper Insights</h3><p>Get quick, structured insights from any paper.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>

          <div className="mla-free-tools-group">
            <h3 className="mla-free-tools-group-title">Other Free Citation Generators</h3>
            <Reveal className="mla-grid-6">
              <Link className="mla-free-tool-card" to="/citation-generator/apa">
                <div><h3>APA Citation Generator</h3><p>Format references in APA style instantly.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/citation-generator/harvard">
                <div><h3>Harvard Citation Generator</h3><p>Create author-date Harvard references.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/citation-generator/chicago">
                <div><h3>Chicago Citation Generator</h3><p>Build Notes-Bibliography or Author-Date citations.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/citation-generator/bibtex">
                <div><h3>BibTeX Generator</h3><p>Get ready-to-use BibTeX entries for LaTeX.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
              <Link className="mla-free-tool-card" to="/citation-generator/metadata">
                <div><h3>Metadata Lookup</h3><p>View a source's structured details before citing it.</p></div>
                <span className="mla-tool-arrow">→</span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mla-section">
        <div className="mla-wrap">
          <Reveal className="mla-cta-inner">
            <h2>Ready to generate your MLA citation?</h2>
            <p>Paste a link, DOI, or title and get a formatted Works Cited entry in seconds, with no sign-up required to try it.</p>
            <a href="#citation-tool" className="mla-btn mla-btn-solid">Generate My MLA Citation Free</a>
          </Reveal>
        </div>
      </section>

      <section className="mla-section" id="mla-faq">
        <div className="mla-wrap">
          <Reveal className="mla-section-head">
            <span className="mla-eyebrow">Questions, Answered</span>
            <h2>Frequently asked questions</h2>
            <p>Everything students ask before using an MLA online citation generator for the first time.</p>
          </Reveal>
          <MlaFaqList />
        </div>
      </section>
    </div>
  );
};

export default MlaCitationContent;
